import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import PoweredBy from '../PoweredBy';

// PIC Control Plane API URL
const PIC_API_URL = process.env.REACT_APP_PIC_API_URL || 'https://agentic-commerce.pentatonic.com';

const LululemonConfirmationView: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useAppContext();
  const hasRecorded = useRef(false);

  // Calculate total trade-in value
  const totalTradeInValue = state.empties.reduce((sum, empty) => {
    if (empty.product.rewards) {
      return sum + parseFloat(empty.product.rewards.dollarValue.replace('$', ''));
    }
    return sum;
  }, 0);

  const itemCount = state.empties.length;
  const claimedOffer = state.hasClaimedOffer;

  // Send takeback event to PIC on mount
  useEffect(() => {
    if (hasRecorded.current) return;
    hasRecorded.current = true;

    const recordTakeback = async () => {
      try {
        // Use a consistent session ID (store in localStorage to persist across page loads)
        let sessionId = localStorage.getItem('pin_session_id');
        if (!sessionId) {
          sessionId = `pin_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          localStorage.setItem('pin_session_id', sessionId);
        }
        
        // Create/update session
        await fetch(`${PIC_API_URL}/api/v1/pin/events`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId,
            storeId: 'lululemon-short-hills',
            kioskId: 'pin-nj-001',
            eventType: 'trade_in_started',
            customerName: state.userEmail ? state.userEmail.split('@')[0] : 'Guest',
            customerEmail: state.userEmail,
            customerId: state.userEmail ? state.userEmail.split('@')[0] : null,
          }),
        });

        // Record each product scanned
        for (const empty of state.empties) {
          await fetch(`${PIC_API_URL}/api/v1/pin/events`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sessionId,
              storeId: 'lululemon-short-hills',
              kioskId: 'pin-nj-001',
              eventType: 'product_scanned',
              productId: empty.product.id,
              eventData: {
                productId: empty.product.id,
                productName: empty.product.name,
                productImage: empty.product.image,
                condition: 'good',
                creditValue: empty.product.rewards ? parseFloat(empty.product.rewards.dollarValue.replace('$', '')) : 0,
              },
            }),
          });
        }

        // Record completion with offer status - this is the critical event
        const completionEventData = {
          creditValue: totalTradeInValue,
          itemCount,
          items: state.empties.map(empty => ({
            productId: empty.product.id,
            productName: empty.product.name,
            productImage: empty.product.image,
            condition: 'good',
            creditValue: empty.product.rewards ? parseFloat(empty.product.rewards.dollarValue.replace('$', '')) : 0,
          })),
          purchaseTotal: claimedOffer ? totalTradeInValue * 2.5 : 0,
          type_name: 'lululemon_trade_in',
          currency: 'USD',
        };

        const completionResponse = await fetch(`${PIC_API_URL}/api/v1/pin/events`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId,
            storeId: 'lululemon-short-hills',
            kioskId: 'pin-nj-001',
            eventType: 'trade_in_completed',
            customerId: state.userEmail ? state.userEmail.split('@')[0] : null,
            customerEmail: state.userEmail,
            customerName: state.userEmail ? state.userEmail.split('@')[0] : 'Guest',
            eventData: completionEventData,
          }),
        });

        if (completionResponse.ok) {
          console.log('[PIN] Takeback completed and recorded to PIC:', sessionId);
          // Clear session ID after successful completion
          localStorage.removeItem('pin_session_id');
        } else {
          console.error('[PIN] Failed to record completion:', await completionResponse.text());
        }
      } catch (error) {
        console.error('[PIN] Failed to record takeback:', error);
      }
    };

    recordTakeback();
  }, [state.empties, state.userEmail, claimedOffer, totalTradeInValue, itemCount]);

  const handleFinish = () => {
    dispatch({ type: 'RESET' });
    navigate('/lululemon');
  };

  const handleVoiceAssistant = () => {
    // Open voice assistant for further help
    const isLocal = window.location.hostname === 'localhost';
    const voiceBaseUrl = isLocal ? 'http://localhost:3002' : 'https://lululemon.voice.pentatonic.com';
    const sessionUrl = `${voiceBaseUrl}/?session=post-takeback-${Date.now()}&store=lululemon-shorthills`;
    window.open(sessionUrl, '_blank');
  };

  return (
    <motion.div
      className="min-h-screen flex flex-col relative overflow-hidden"
      style={{ backgroundColor: '#1A1A1A' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Background gradient - success green tint */}
      <div 
        className="absolute inset-0 opacity-15"
        style={{
          background: 'radial-gradient(ellipse at 50% 30%, #22C55E 0%, transparent 70%)'
        }}
      />

      {/* Confetti / Celebration animation */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-3 h-3 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              backgroundColor: ['#D31334', '#22C55E', '#FFFFFF', '#F59E0B'][Math.floor(Math.random() * 4)],
            }}
            initial={{ 
              y: -20, 
              opacity: 0,
              scale: 0
            }}
            animate={{ 
              y: window.innerHeight + 20,
              opacity: [0, 1, 1, 0],
              scale: [0, 1, 1, 0.5],
              rotate: Math.random() * 360
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              delay: Math.random() * 2,
              ease: 'linear',
              repeat: Infinity,
              repeatDelay: Math.random() * 3
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col">
        {/* Header */}
        <div className="flex justify-center mx-20 mt-10">
          <div className="w-full max-w-[920px] flex justify-center">
            <img 
              src="/assets/images/logos/lululemon-white.png" 
              alt="lululemon" 
              className="h-8"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col items-center justify-center px-20">
          {/* Success Icon */}
          <motion.div
            className="w-32 h-32 rounded-full flex items-center justify-center mb-8"
            style={{ backgroundColor: 'rgba(34,197,94,0.2)' }}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', delay: 0.2 }}
          >
            <motion.svg 
              className="w-16 h-16" 
              fill="none" 
              stroke="#22C55E" 
              viewBox="0 0 24 24"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </motion.svg>
          </motion.div>

          {/* Thank You Message */}
          <motion.div
            className="text-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h1 
              className="text-6xl font-light mb-4"
              style={{ color: '#FFFFFF', fontFamily: "'Calibre', 'Helvetica Neue', sans-serif" }}
            >
              Thank You!
            </h1>
            <p 
              className="text-2xl font-light"
              style={{ color: 'rgba(255,255,255,0.7)' }}
            >
              You're making a difference
            </p>
          </motion.div>

          {/* Summary Card */}
          <motion.div
            className="w-full max-w-lg rounded-2xl p-8 mb-8"
            style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="text-center mb-6">
              <p className="text-white/60 text-sm uppercase tracking-wider mb-2">Your Trade-In</p>
              <p className="text-white text-lg">
                {itemCount} {itemCount === 1 ? 'item' : 'items'} accepted
              </p>
            </div>

            <div className="border-t border-white/10 pt-6 text-center">
              <p className="text-white/60 text-sm uppercase tracking-wider mb-2">Credit Earned</p>
              <p className="text-5xl font-bold" style={{ color: '#22C55E' }}>
                ${totalTradeInValue}
              </p>
            </div>

            {claimedOffer && (
              <div className="border-t border-white/10 pt-6 mt-6 text-center">
                <p className="text-white/60 text-sm uppercase tracking-wider mb-2">Applied To</p>
                <p className="text-white text-lg">Your new purchase</p>
              </div>
            )}
          </motion.div>

          {/* What Happens Next */}
          <motion.div
            className="text-center mb-12 max-w-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <h3 className="text-white text-lg font-medium mb-4">What happens next?</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div 
                  className="w-12 h-12 mx-auto mb-2 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                >
                  <span className="text-white font-medium">1</span>
                </div>
                <p className="text-white/60 text-sm">Items are inspected</p>
              </div>
              <div className="text-center">
                <div 
                  className="w-12 h-12 mx-auto mb-2 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                >
                  <span className="text-white font-medium">2</span>
                </div>
                <p className="text-white/60 text-sm">Cleaned & restored</p>
              </div>
              <div className="text-center">
                <div 
                  className="w-12 h-12 mx-auto mb-2 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                >
                  <span className="text-white font-medium">3</span>
                </div>
                <p className="text-white/60 text-sm">Resold at lower price</p>
              </div>
            </div>
          </motion.div>

          {/* Actions */}
          <motion.div
            className="flex flex-col items-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <button
              onClick={handleFinish}
              className="px-12 py-4 rounded-lg text-white uppercase tracking-wider font-medium"
              style={{ backgroundColor: '#D31334' }}
            >
              Done
            </button>
            <button
              onClick={handleVoiceAssistant}
              className="text-white/50 hover:text-white text-sm uppercase tracking-wider transition-colors"
            >
              Need Help? Talk to AI
            </button>
          </motion.div>
        </div>

        {/* Footer */}
        <div className="flex justify-center pb-8">
          <PoweredBy />
        </div>
      </div>
    </motion.div>
  );
};

export default LululemonConfirmationView;

